export type PillPaletteToken =
  | "blueLight1"
  | "blueLight2"
  | "blueBright"
  | "cyanLight1"
  | "cyanLight2"
  | "cyanBright"
  | "greenLight1"
  | "greenLight2"
  | "greenBright"
  | "yellowLight1"
  | "yellowLight2"
  | "orangeLight1"
  | "orangeLight2"
  | "orangeBright"
  | "redLight1"
  | "redLight2"
  | "redBright"
  | "pinkLight1"
  | "pinkLight2"
  | "purpleLight1"
  | "purpleLight2"
  | "purpleBright"
  | "tealLight1"
  | "tealLight2"
  | "grayLight2"
  | "neutral";

export const pillPaletteTokens: readonly PillPaletteToken[] = [
  "blueLight1",
  "blueLight2",
  "blueBright",
  "cyanLight1",
  "cyanLight2",
  "cyanBright",
  "greenLight1",
  "greenLight2",
  "greenBright",
  "yellowLight1",
  "yellowLight2",
  "orangeLight1",
  "orangeLight2",
  "orangeBright",
  "redLight1",
  "redLight2",
  "redBright",
  "pinkLight1",
  "pinkLight2",
  "purpleLight1",
  "purpleLight2",
  "purpleBright",
  "tealLight1",
  "tealLight2",
  "grayLight2",
  "neutral",
];

const copingStrategyTokens: Readonly<Record<string, PillPaletteToken>> = {
  "5 Senses (5-4-3-2-1)": "yellowLight2",
  "ABC Game": "orangeLight2",
  "Box Breathing": "tealLight1",
  "Deep Breathing": "blueLight2",
  "Delaying/Reducing Rituals": "greenLight2",
  "Do Something Else": "cyanLight1",
  "Hills and Valleys Breathing": "cyanLight2",
  "Imagery - Go to your happy place": "purpleLight1",
  "Leaves on a Stream": "tealLight2",
  "Mindful Search (by Color, Shape, or Texture)": "redLight2",
  "Paced Breathing": "blueLight2",
  Praying: "blueLight1",
  "Progressive Muscle Relaxation": "cyanLight2",
  "Removing Self from the Room": "yellowLight2",
  "Self-Soothing the Five Senses": "pinkLight1",
  "Talk with Parents": "tealLight1",
  "Target Game": "purpleLight2",
  "TIPP Skills": "pinkLight2",
  "My Go-To Coping Skills": "greenLight1",
  "My Personalized Coping Strategy": "yellowLight1",
};

const funActivityTokens: Readonly<Record<string, PillPaletteToken>> = {
  Baking: "yellowLight2",
  "Doing Crafts": "redLight2",
  Exercising: "purpleLight2",
  "Going to the Park": "grayLight2",
  "Listening to Music": "redLight2",
  "Taking Photos": "blueLight2",
  "Playing Computer Games": "tealLight2",
  "Playing Games": "cyanLight2",
  "Playing an Instrument": "blueLight1",
  "Playing Piano": "pinkLight2",
  "Reading a Book": "greenLight2",
  Running: "grayLight2",
  Singing: "cyanLight2",
  "Solving Puzzles": "greenLight2",
  Swimming: "purpleLight2",
  "Spending Time with Pets": "orangeLight2",
  "Visiting Friends": "orangeLight2",
  "Visiting Museums": "pinkLight2",
  Walking: "blueLight2",
  "Walking the Dog": "yellowLight2",
  "Watching Movies": "tealLight2",
};

const badgeTokens: Readonly<Record<string, PillPaletteToken>> = {
  ariel: "greenLight2",
  aurora: "tealLight2",
  belle: "yellowLight2",
  cinderella: "cyanLight2",
  jasmine: "orangeLight2",
  merida: "grayLight2",
  moana: "blueLight1",
  mulan: "pinkLight2",
  pocahontas: "redLight2",
  rapunzel: "cyanLight1",
  raya: "tealLight1",
  snowwhite: "blueLight2",
  tiana: "purpleLight2",
  superhero_batman: "greenLight1",
  superhero_captainamerica: "yellowLight1",
  superhero_hulk: "orangeLight1",
  superhero_ironman: "redLight1",
  superhero_spiderman: "pinkLight1",
  superhero_superman: "purpleLight1",
  superhero_thor: "blueBright",
  superhero_wolverine: "cyanBright",
  pokemon_celebi: "orangeBright",
  pokemon_gastly: "purpleBright",
};

const activityTypeTokens: Readonly<Record<string, PillPaletteToken>> = {
  "Anxiety Body Map": "greenLight1",
  Assessment: "blueLight1",
  "Behavioral Activation": "redLight1",
  CBT: "greenLight2",
  "Checking OCD": "cyanLight2",
  "Contamination OCD": "blueLight2",
  "Coping Skill Practice": "pinkLight1",
  "Counting / Repeating OCD": "greenLight2",
  "Daily Mood Check": "purpleLight1",
  "Eating Related OCD": "pinkLight2",
  "Existential / Philosophical OCD": "yellowLight2",
  "Expectancy Tracker": "purpleBright",
  Exposure: "cyanLight1",
  "False Memory OCD": "orangeLight2",
  "Harm OCD": "pinkLight2",
  Hoarding: "tealLight1",
  "Imaginal Exposure": "cyanBright",
  "Just Right OCD": "tealLight2",
  "Magic Thinking OCD": "redLight2",
  Other: "greenLight1",
  "Perfectionism OCD": "yellowLight2",
  Psychoeducation: "orangeLight2",
  Reflection: "orangeLight1",
  "Relationship OCD (ROCD)": "cyanLight2",
  "Religious / Scrupulosity OCD": "blueLight2",
  Report: "yellowLight1",
  "Sensorimotor / Somatic OCD": "redLight2",
  "Sexual / Taboo OCD": "grayLight2",
  "Sexual Orientation (SO-OCD)": "tealLight2",
  "Symmetry OCD": "purpleLight2",
  "Youth Top Problems for Client": "tealLight1",
  "Youth Top Problems for Parents": "blueBright",
  "Values Card Sort": "orangeBright",
};

export function getActivityTypePillToken(
  value: string,
  colorKey?: string,
): PillPaletteToken {
  if (pillPaletteTokens.includes(colorKey as PillPaletteToken)) {
    return colorKey as PillPaletteToken;
  }
  return activityTypeTokens[value] ?? "neutral";
}

export function getConfiguredPillToken(colorKey?: string): PillPaletteToken {
  return pillPaletteTokens.includes(colorKey as PillPaletteToken)
    ? (colorKey as PillPaletteToken)
    : "grayLight2";
}

const homeworkCompletionStatusTokens: Readonly<
  Record<string, PillPaletteToken>
> = {
  "Not Completed": "yellowLight1",
  Completed: "greenBright",
  "Completed with Modification": "greenLight2",
  Attempted: "cyanLight1",
  Missed: "redBright",
};

export function getHomeworkCompletionStatusPillToken(
  status: string,
): PillPaletteToken {
  return homeworkCompletionStatusTokens[status] ?? "grayLight2";
}

const clinicianRoleTokens: Readonly<Record<string, PillPaletteToken>> = {
  admin: "blueLight1",
  supervisor: "purpleLight1",
  therapist: "greenLight1",
};

export function getClinicianRolePillToken(role: string): PillPaletteToken {
  return clinicianRoleTokens[role.trim().toLocaleLowerCase()] ?? "grayLight2";
}

const homeworkRepeatTokens: Readonly<Record<string, PillPaletteToken>> = {
  "": "grayLight2",
  Daily: "cyanLight1",
  Weekly: "purpleLight1",
};

export function getHomeworkRepeatPillToken(
  repeats: string,
): PillPaletteToken {
  return homeworkRepeatTokens[repeats] ?? "grayLight2";
}

const weekdayTokens: Readonly<Record<string, PillPaletteToken>> = {
  Monday: "blueLight1",
  Tuesday: "cyanLight1",
  Wednesday: "greenLight1",
  Thursday: "yellowLight1",
  Friday: "orangeLight1",
  Saturday: "pinkLight1",
  Sunday: "purpleLight1",
};

export function getWeekdayPillToken(weekday: string): PillPaletteToken {
  return weekdayTokens[weekday] ?? "grayLight2";
}

export function getValuePillToken(
  value: string,
  kind: "coping" | "fun" | "badge",
): PillPaletteToken {
  const registry =
    kind === "coping"
      ? copingStrategyTokens
      : kind === "fun"
        ? funActivityTokens
        : badgeTokens;
  return registry[value] ?? "neutral";
}

export function getCopingStrategyPillToken(
  value: string,
  optionColorKey?: string,
  clientColorKey?: string,
): PillPaletteToken {
  return getConfiguredValuePillToken(
    value,
    "coping",
    optionColorKey,
    clientColorKey,
  );
}

export function getConfiguredValuePillToken(
  value: string,
  kind: "coping" | "fun" | "badge",
  optionColorKey?: string,
  clientColorKey?: string,
  alternateValue?: string,
): PillPaletteToken {
  const configuredColor = [optionColorKey, clientColorKey].find((colorKey) =>
    pillPaletteTokens.includes(colorKey as PillPaletteToken),
  );
  if (configuredColor) return configuredColor as PillPaletteToken;
  const sharedToken = [value, alternateValue]
    .filter((candidate): candidate is string => Boolean(candidate))
    .map((candidate) => getValuePillToken(candidate, kind))
    .find((token) => token !== "neutral");
  return sharedToken ?? "grayLight2";
}
